/**
 * Tier Feature Definitions
 * 
 * Defines which features are available at each subscription tier.
 * Features are cumulative - higher tiers inherit lower tier features.
 * 
 * MUST stay in sync with backend: apps/api/src/middleware/tier-access.ts
 */

export const TIER_FEATURES = {
  directory_presence: [
    'directory_entry_enabled',
    'directory_entry_layout_classic',
    'directory_entry_hours_on',
    'directory_entry_map_on',
    'directory_entry_contact_on',
    'directory_entry_qr_on',
    'directory_entry_whatsapp_on',
    'storefront_enabled',
    'storefront_retail',
    'directory_visibility_snap_ebt',
  ],
  // V3.1 Entry Presence — directory mode (display: "Starter")
  // Clean key `presence` — does NOT inherit google_only or legacy starter feature bag.
  presence: [
    'directory_entry_enabled',
    'directory_entry_layout_classic',
    'directory_entry_layout_editorial',
    'directory_entry_layout_immersive',
    'directory_entry_logo_on',
    'directory_entry_about_on',
    'directory_entry_gallery_on',
    'directory_entry_social_on',
    'directory_entry_hours_on',
    'directory_entry_map_on',
    'directory_entry_contact_on',
    'directory_entry_qr_on',
    'directory_entry_whatsapp_on',
    'storefront_enabled',
    'storefront_retail',
  ],
  google_only: [
    'google_shopping',
    'google_merchant_center',
    'basic_product_pages',
    'qr_codes_512',
    'performance_analytics',
    // NOTE: No 'storefront' or quick starts - google_only is maintenance mode
  ],
  starter: [
    'storefront',
    'product_search',
    'mobile_responsive',
    'enhanced_seo',
    'basic_categories',
    'category_quick_start',      // Generate starter categories (Starter+)
    'image_finder',              // Find stock photos for products (Starter+)
    'clover_pos',                // Clover POS integration (Starter+)
  ],
  professional: [
    // ⚠️ CRITICAL REVENUE-PROTECTING FEATURES
    'quick_start_wizard',       // Saves 400+ hours, worth $10K+
    'barcode_scan',          // DEPRECATED: superseded by barcode_scan_options capability. Still used by TierGate fallback.
    'gbp_integration',           // Worth $200-300/mo
    'custom_branding',
    'business_logo',
    'qr_codes_1024',
    'image_gallery_5',
    'interactive_maps',
    'privacy_mode',
    'custom_marketing_copy',
    'priority_support',
    'square_pos',                // Square POS integration (Pro+)
    'payment_client_credentials', // Direct API credentials for payment gateways (Pro+)
  ],
  enterprise: [
    'unlimited_skus',
    'white_label',
    'custom_domain',
    'qr_codes_2048',
    'image_gallery_10',
    'api_access',
    'advanced_analytics',
    'dedicated_account_manager',
    'sla_guarantee',
    'custom_integrations',
    'customDomains', // Custom domain hosting for GMC compliance
  ],
  organization: [
    // Organization-specific features (franchise model)
    'propagation_products',
    'propagation_categories',
    'propagation_gbp_sync',
    'propagation_hours',
    'propagation_profile',
    'propagation_flags',
    'propagation_roles',
    'propagation_brand',
    'organization_dashboard',
    'hero_location',
    'strategic_testing',
    'unlimited_locations',
    'shared_sku_pool',
    'centralized_control',
    'api_access',
  ],
  // Chain tiers (similar to individual but multi-location)
  chain_starter: [
    'storefront',
    'product_search',
    'mobile_responsive',
    'enhanced_seo',
    'multi_location_5',
  ],
  chain_professional: [
    'quick_start_wizard',
    'barcode_scan',           // DEPRECATED: superseded by barcode_scan_options capability. Still used by TierGate fallback.
    'gbp_integration',
    'custom_branding',
    'qr_codes_1024',
    'image_gallery_5',
    'multi_location_25',
    'basic_propagation',
  ],
  chain_enterprise: [
    'unlimited_skus',
    'white_label',
    'custom_domain',
    'qr_codes_2048',
    'image_gallery_10',
    'api_access',
    'unlimited_locations',
    'advanced_propagation',
    'dedicated_account_manager',
  ],
} as const;

// Tier hierarchy for inheritance (lower tiers inherit from higher)
// NOTE: 'trial' is not a tier - it's a time-limited status that can apply to any tier
export const TIER_HIERARCHY: Record<string, string[]> = {
  directory_presence: [],
  google_only: ['directory_presence'],
  // V3.1: presence inherits directory_presence ONLY (no google_only)
  presence: ['directory_presence'],
  starter: ['google_only', 'directory_presence'],
  discovery: ['google_only', 'directory_presence'],
  storefront: ['discovery', 'google_only', 'directory_presence'],
  commitment: ['storefront', 'discovery', 'google_only', 'directory_presence'],
  ecommerce: ['storefront', 'discovery', 'google_only', 'directory_presence'],
  omnichannel: ['commitment', 'ecommerce', 'storefront', 'discovery', 'google_only', 'directory_presence'],
  professional: ['omnichannel', 'commitment', 'ecommerce', 'storefront', 'discovery', 'google_only', 'directory_presence'],
  enterprise: ['professional', 'omnichannel', 'commitment', 'ecommerce', 'storefront', 'discovery', 'google_only', 'directory_presence'],
  organization: ['professional', 'omnichannel', 'commitment', 'ecommerce', 'storefront', 'discovery', 'google_only', 'directory_presence'],
  chain_starter: ['starter', 'google_only', 'directory_presence'],
  chain_professional: ['professional', 'omnichannel', 'commitment', 'ecommerce', 'storefront', 'discovery', 'google_only', 'directory_presence'],
  chain_enterprise: ['enterprise', 'professional', 'omnichannel', 'commitment', 'ecommerce', 'storefront', 'discovery', 'google_only', 'directory_presence'],
};

// Feature to minimum required tier mapping
export const FEATURE_TIER_MAP: Record<string, string> = {
  // Starter tier features
  storefront: 'starter',
  product_search: 'starter',
  mobile_responsive: 'starter',
  enhanced_seo: 'starter',
  category_quick_start: 'starter',
  image_finder: 'starter',
  clover_pos: 'starter',
  
  // Professional tier features (CRITICAL)
  quick_start_wizard: 'professional',
  barcode_scan: 'professional',  // DEPRECATED: superseded by barcode_scan_options capability
  gbp_integration: 'professional',
  custom_branding: 'professional',
  qr_codes_1024: 'professional',
  image_gallery_5: 'professional',
  square_pos: 'professional',
  payment_client_credentials: 'professional', // Direct API credentials for payment gateways
  
  // Enterprise tier features
  white_label: 'enterprise',
  custom_domain: 'enterprise',
  qr_codes_2048: 'enterprise',
  image_gallery_10: 'enterprise',
  api_access: 'enterprise',
  
  // Starter tier propagation (limited)
  propagation_products: 'starter',
  propagation_user_roles: 'starter',
  
  // Professional tier propagation (full operational suite)
  propagation_hours: 'professional',
  propagation_profile: 'professional',
  propagation_categories: 'professional',
  propagation_gbp_sync: 'professional',
  propagation_feature_flags: 'professional',
  
  // Organization tier features
  propagation_brand_assets: 'organization',
  propagation_selective: 'organization',
  propagation_scheduling: 'organization',
  propagation_rollback: 'organization',
  organization_dashboard: 'organization',
  hero_location: 'organization',
  // Integrations (canonical keys)
  integration_enabled: 'discovery',
  integration_google_enabled: 'discovery',
  integration_google_shopping: 'discovery',
  integration_google_merchant_center: 'discovery',
  integration_clover: 'starter',
  integration_gmc_sync: 'commitment',
  integration_square: 'ecommerce',
  integration_gbp: 'ecommerce',
  integration_pos_enabled: 'ecommerce',
  integration_flexible: 'enterprise',
  integration_propagation_gbp: 'organization',
};

// Tier display names
// NOTE: 'trial' is not a tier - it's a subscription status (trial, active, past_due, canceled)
export const TIER_DISPLAY_NAMES: Record<string, string> = {
  google_only: 'Google-Only',
  directory_presence: 'Directory Presence',
  presence: 'Starter',
  starter: 'Starter',
  discovery: 'Discovery',
  storefront: 'Storefront',
  commitment: 'Commitment',
  ecommerce: 'E-commerce',
  omnichannel: 'Omnichannel',
  professional: 'Professional',
  enterprise: 'Enterprise',
  organization: 'Organization',
  chain_starter: 'Chain Starter',
  chain_professional: 'Chain Professional',
  chain_enterprise: 'Chain Enterprise',
};

/**
 * Human-readable feature names for display in UI
 */
export const FEATURE_DISPLAY_NAMES: Record<string, string> = {
  // Core features
  'storefront': 'Public Storefront',
  'quick_start_wizard': 'Product Quick Start',
  'quick_start_wizard_limited': 'Product Quick Start (Limited)',
  'quick_start_wizard_full': 'Product Quick Start (Full Access)',
  'category_quick_start': 'Category Quick Start',
  'image_finder': 'Product Image Finder',
  'barcode_scan': 'Product Scanning',  // DEPRECATED: superseded by barcode_scan_options capability
  'gbp_integration': 'Google Business Profile Integration',
  'api_access': 'API Access',
  'white_label': 'White Label Branding',
  'custom_domain': 'Custom Domain',
  'advanced_analytics': 'Advanced Analytics',
  
  // Product features
  'product_search': 'Product Search',
  'basic_product_pages': 'Basic Product Pages',
  'image_gallery_5': '5-Image Gallery',
  'image_gallery_10': '10-Image Gallery',
  
  // Branding
  'custom_branding': 'Custom Branding',
  'business_logo': 'Business Logo',
  'custom_marketing_copy': 'Custom Marketing Copy',
  
  // Categories
  'basic_categories': 'Basic Categories',
  
  // QR Codes
  'qr_codes_512': 'QR Codes (512px)',
  'qr_codes_1024': 'QR Codes (1024px)',
  'qr_codes_2048': 'QR Codes (2048px)',
  
  // Google features
  'google_shopping': 'Google Shopping Feed',
  'google_merchant_center': 'Google Merchant Center',
  
  // UI features
  'mobile_responsive': 'Mobile-Responsive Design',
  'enhanced_seo': 'Enhanced SEO',
  'interactive_maps': 'Interactive Maps',
  'privacy_mode': 'Privacy Mode',
  
  // Support
  'priority_support': 'Priority Support',
  'dedicated_account_manager': 'Dedicated Account Manager',
  'sla_guarantee': 'SLA Guarantee',
  
  // Analytics
  'performance_analytics': 'Performance Analytics',
  
  // SKU limits
  'unlimited_skus': 'Unlimited SKUs',
  
  // Integrations
  'custom_integrations': 'Custom Integrations',
  'clover_pos': 'Clover POS Integration',
  'square_pos': 'Square POS Integration',
  'payment_client_credentials': 'Direct Payment API Credentials',
  
  // Multi-location
  'multi_location_5': '5 Locations',
  'multi_location_25': '25 Locations',
  'unlimited_locations': 'Unlimited Locations',
  
  // Organization features
  'propagation_products': 'Product Propagation',
  'propagation_categories': 'Category Propagation',
  'propagation_gbp_sync': 'GBP Sync Propagation',
  'propagation_hours': 'Hours Propagation',
  'propagation_profile': 'Profile Propagation',
  'propagation_flags': 'Flag Propagation',
  'propagation_roles': 'Role Propagation',
  'propagation_brand': 'Brand Propagation',
  'organization_dashboard': 'Organization Dashboard',
  'hero_location': 'Hero Location',
  'strategic_testing': 'Strategic Testing',
  'shared_sku_pool': 'Shared SKU Pool',
  'centralized_control': 'Centralized Control',
  // Integration options (canonical keys)
  'integration_enabled': 'Integrations Enabled',
  'integration_clover': 'Clover POS Integration',
  'integration_square': 'Square POS Integration',
  'integration_gbp': 'Google Business Profile',
  'integration_google_shopping': 'Google Shopping Feed',
  'integration_google_merchant_center': 'Google Merchant Center',
  'integration_gmc_sync': 'Advanced GMC Sync',
  'integration_propagation_gbp': 'GBP Propagation',
  'integration_flexible': 'Integration Flexible Mode',
  'integration_pos_enabled': 'POS Integrations',
  'integration_google_enabled': 'Google Integrations',

  // Commerce types
  'commerce_full_payment': 'Full Payment',
  'commerce_deposit_only': 'Deposit Only',
  'commerce_flexible': 'Commerce Flexible',

  // Featured options
  'featured_store_selection': 'Store Selection',
  'featured_new_arrival': 'New Arrival',
  'featured_seasonal': 'Seasonal',
  'featured_sale': 'Sale',
  'featured_staff_pick': 'Staff Pick',
  'featured_clearance': 'Clearance',
  'featured_featured': 'Featured',
  'featured_bestseller': 'Bestseller',
  'featured_trending': 'Trending',
  'featured_recommended': 'Recommended',
  'featured_random_featured': 'Random Featured',
  'featured_expiry_monitor': 'Expiry Monitor',
  'featured_tenant_enabled': 'Tenant Badges',
  'featured_platform_enabled': 'Platform Badges',
  'featured_flexible': 'Featured Flexible',

  // Directory Promotion
  'directory_promotion_level_basic': 'Promotion: Basic',
  'directory_promotion_level_premium': 'Promotion: Premium',
  'directory_promotion_level_featured': 'Promotion: Featured',
  'directory_promotion_flexible': 'Directory Promotion Flexible',
  'directory_promotion_enabled': 'Directory Promotion Enabled',

  // Directory Entry WhatsApp CTA
  'directory_entry_whatsapp_on': 'WhatsApp Chat Button',
  'directory_entry_whatsapp_enabled': 'WhatsApp Chat Button',
};

// Tier pricing (monthly)
// NOTE: All tiers can be trialed for 14 days before payment is required
export const TIER_PRICING: Record<string, number> = {
  google_only: 29,
  directory_presence: 0,
  presence: 19,
  discovery: 29,
  storefront: 59,
  commitment: 79,  // V2 pricing
  ecommerce: 99,   // V2 new tier
  omnichannel: 149, // V2 new tier
  professional: 199,
  enterprise: 499,  // V2 pricing
  organization: 499, // V2 pricing
  chain_starter: 199,
  chain_professional: 1999,
  chain_enterprise: 4999,
};

/**
* Feature-specific limits per tier
* Used to restrict "taste test" features for lower tiers
*/
export const TIER_FEATURE_LIMITS: Record<string, Record<string, any>> = {
  
starter: {
category_quick_start: {
maxCategories: 15,         // Up to 15 categories
rateLimitDays: 7,          // Once per 7 days
businessTypes: ['grocery', 'fashion', 'electronics', 'general'], // All types
},
},
professional: {
quick_start_wizard: {
maxProducts: 100,          // Full access: up to 100 products
rateLimitDays: 1,          // Once per day
scenarios: ['grocery', 'fashion', 'electronics', 'general'], // All scenarios
},
category_quick_start: {
maxCategories: 30,         // Up to 30 categories
rateLimitDays: 1,          // Once per day
businessTypes: ['grocery', 'fashion', 'electronics', 'general'], // All types
},
},
enterprise: {
quick_start_wizard: {
maxProducts: 100,
rateLimitDays: 1,
scenarios: ['grocery', 'fashion', 'electronics', 'general'],
},
category_quick_start: {
maxCategories: 30,
rateLimitDays: 1,
businessTypes: ['grocery', 'fashion', 'electronics', 'general'],
},
},
};

/**
* Check if a tier has access to a feature
*/
export function checkTierFeature(tier: string, feature: string): boolean {
  // Get features for this tier
  const tierFeatures = TIER_FEATURES[tier as keyof typeof TIER_FEATURES] || [];
  
  // Check if feature is in this tier
  if ((tierFeatures as readonly string[]).includes(feature)) {
    return true;
  }
  
  // Check inherited tiers
  const inheritedTiers = TIER_HIERARCHY[tier] || [];
  for (const inheritedTier of inheritedTiers) {
    const inheritedFeatures = TIER_FEATURES[inheritedTier as keyof typeof TIER_FEATURES] || [];
    if ((inheritedFeatures as readonly string[]).includes(feature)) {
      return true;
    }
  }
  
  return false;
}

/**
 * Get the minimum required tier for a feature
 */
export function getRequiredTier(feature: string): string {
  return FEATURE_TIER_MAP[feature] || 'professional';
}

/**
 * Get tier display name
 */
export function getTierDisplayName(tier: string): string {
  return TIER_DISPLAY_NAMES[tier] || tier;
}

/**
 * Get tier pricing
 */
export function getTierPricing(tier: string): number {
  return TIER_PRICING[tier] || 0;
}

/**
 * Get all available features for a tier (including inherited)
 */
export function getTierFeatures(tier: string): string[] {
  const features = new Set<string>();
  
  // Add tier's own features
  const tierFeatures = TIER_FEATURES[tier as keyof typeof TIER_FEATURES] || [];
  (tierFeatures as readonly string[]).forEach(f => features.add(f));
  
  // Add inherited features
  const inheritedTiers = TIER_HIERARCHY[tier] || [];
  for (const inheritedTier of inheritedTiers) {
    const inheritedFeatures = TIER_FEATURES[inheritedTier as keyof typeof TIER_FEATURES] || [];
    (inheritedFeatures as readonly string[]).forEach(f => features.add(f));
  }
  
  return Array.from(features);
}

/**
 * Get feature limits for a specific tier and feature
 * Returns null if no limits apply (full access)
 */
export function getFeatureLimits(tier: string, feature: string): any | null {
  const tierLimits = TIER_FEATURE_LIMITS[tier];
  if (!tierLimits) return null;
  
  return tierLimits[feature] || null;
}

/**
 * Calculate upgrade requirements for a feature
 */
export function calculateUpgradeRequirements(currentTier: string, feature: string): {
  required: boolean;
  targetTier?: string;
  targetTierDisplay?: string;
  targetPrice?: number;
  currentPrice?: number;
  upgradeCost?: number;
} {
  const hasAccess = checkTierFeature(currentTier, feature);
  
  if (hasAccess) {
    return { required: false };
  }
  
  const targetTier = getRequiredTier(feature);
  const targetTierDisplay = getTierDisplayName(targetTier);
  const targetPrice = getTierPricing(targetTier);
  const currentPrice = getTierPricing(currentTier);
  const upgradeCost = targetPrice - currentPrice;
  
  return {
    required: true,
    targetTier,
    targetTierDisplay,
    targetPrice,
    currentPrice,
    upgradeCost,
  };
}
